
import React, { useCallback, useState } from 'react';
import {
    View,
    ScrollView,
    TouchableOpacity,
    Image,
    ActivityIndicator
} from 'react-native';

import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import moment from 'moment';
import PlateImage from '../../../components/lpr/PlateImage';
import { appAgentService } from '../../../services/central-api/app-agent';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import alertAudioFile from './../../../../assets/alert.mp3';
import { RootNavigation } from '../../../Routes';

const sound = new Audio(alertAudioFile);

interface AgentOccurrencesParams {
    navigation: RootNavigation;
}


export default function AgentOccurrences({ navigation }: AgentOccurrencesParams) {
    const { styles, theme } = useStyles(styleSheet);
    const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    function hasSameItems(a: number[], b: number[]) {
        if (a.length !== b.length) {
            return false;
        }
        const aSorted = [...a].sort();
        const bSorted = [...b].sort();
        return aSorted.every((val, i) => val === bSorted[i]);
    }

    async function getOccurrences(): Promise<void> {
        try {
            if (!occurrences) {
                setIsLoading(true);
            }
            const occurrencesRes = await appAgentService.getOccurrences();

            if (!hasSameItems(occurrences.map(o => o.id || 0), occurrencesRes.map(o => o.id || 0))) {
                setOccurrences(occurrencesRes);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    function playAlertSound() {
        sound.currentTime = 0;
        sound.play().catch(err => console.error(err));
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getOccurrences();

            if (occurrences.length > 0) {
                playAlertSound();
            }

            const interval = setInterval(() => {
                getOccurrences();
            }, 5000);

            return () => clearInterval(interval);
        }
    }, [isFocused, navigation, occurrences]));

    function getPriorityDescription(priority: OccurrencePriorities) {
        switch (priority) {
            case 'maximum':
                return translate('maximum_priority');
            case 'high':
                return translate('high_priority');
            case 'medium':
                return translate('medium_priority');
            case 'low':
                return translate('low_priority');
            default:
                return translate('medium_priority');
        }
    }

    function renderItem(occurrence: Occurrence) {
        return <TouchableOpacity key={occurrence.id} onPress={() => {
            if (occurrence.id) {
                navigation.navigate('AppAgent', {
                    screen: 'AgentOccurrenceDetails',
                    params: { occurrenceId: occurrence.id }
                });
            }
        }}>
            <View style={styles.card}>
                <View style={{ flex: 1 }}>
                    <MyAppText style={styles.mainText}>
                        {occurrence.triggerType
                            ? translate(occurrence.triggerType)
                            : occurrence.occurrenceType?.name
                        }
                    </MyAppText>
                    <MyAppText style={styles.dateText}>
                        {moment(occurrence.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                    </MyAppText>
                </View>
                {occurrence.priority
                    ? <View style={styles.itemPriority} >
                        <MyAppText style={occurrence.priority === 'maximum' ? styles.itemMaximumPriorityText : styles.itemPriorityText}>{getPriorityDescription(occurrence.priority)}</MyAppText>
                    </View>
                    : null
                }
                {occurrence.triggerType == 'face_detected'
                    || occurrence.triggerType == 'missing_face_detected'
                    || occurrence.triggerType == 'mp_face_detected' ?
                    <View style={styles.imageBox}>
                        <Image
                            source={{ uri: occurrence.details?.imageUrl }}
                            style={styles.image}
                        />
                    </View>
                    : null}
                {occurrence.details && occurrence.details.imageUrl &&
                    (occurrence.triggerType == 'plate_detected' ||
                        occurrence.triggerType == 'plate_detected_by_mobile_agent' ||
                        occurrence.triggerType == 'cortex_plate_detected' ||
                        occurrence.triggerType == 'clone_suspect' ||
                        occurrence.triggerType == 'irregular_vehicle' ||
                        occurrence.triggerType == 'illegal_debris' ||
                        occurrence.triggerType == 'plate_detected_by_citizen_app'
                    ) ?
                    (occurrence.details.platePosition ? <PlateImage src={occurrence.details.imageUrl} platePosition={occurrence.details.platePosition} /> : <Image
                        source={{ uri: occurrence.details?.imageUrl }}
                        style={styles.lprImage}
                    />)
                    : null
                }
                {occurrence.triggerType == 'sigrc' && occurrence.details?.imageUrl ?
                    <View style={styles.imageBox}>
                        <Image
                            source={{ uri: occurrence.details?.imageUrl }}
                            style={styles.image}
                        />
                    </View>
                    : null}
            </View>
        </TouchableOpacity>;
    }

    return (
        <>
            {isLoading ?
                <View style={styles.loadingView}>
                    <MyAppText style={styles.loadingText}>
                        {translate('loading')}...
                    </MyAppText>
                    <ActivityIndicator
                        animating={true}
                        style={styles.loading}
                        size='large'
                        color='#0071bc'
                    >
                    </ActivityIndicator>
                </View> :

                occurrences.length ?
                    <ScrollView>
                        <View style={styles.container}>
                            {occurrences.map((occurrence) => (
                                renderItem(occurrence)
                            ))}
                        </View>
                    </ScrollView>
                    : <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <MyAppText style={{ color: theme.colors.labelColor }}>
                            {translate('noResults')}.
                        </MyAppText>
                    </View>}
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    loading: {
        marginHorizontal: 'auto',
        transform: [{ scaleX: 2 }, { scaleY: 2 }],
        width: '20%',
        height: '20%',
    },
    loadingText: {
        color: theme.colors.loadingColor,
        textAlign: 'center',
        fontSize: 16
    },
    loadingView: {
        flex: 1,
        justifyContent: 'center',
        alignContent: 'center'
    },
    container: {
        flex: 1,
        padding: 16,
        rowGap: 10
    },
    card: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.card.border,
        padding: 8,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: theme.colors.card.background,
        height: 80,
        flex: 1
    },
    mainText: {
        color: theme.colors.textColor,
        fontSize: 15,
        flex: 1
    },
    dateText: {
        color: theme.colors.card.detailText,
        fontSize: 12,
    },
    image: {
        height: 64,
        width: 64,
        borderRadius: 4,
        objectFit: 'contain',
    },
    lprImage: {
        height: 64,
        width: 100,
        borderRadius: 4,
        objectFit: 'contain',
    },
    imageBox: {
        height: 64,
        width: 64,
        borderRadius: 4,
        justifyContent: 'center',
        alignItems: 'center'
    },
    itemPriority: {
        justifyContent: 'center',
        alignItems: 'center',
        width: 64,
        height: 64,
    },
    itemPriorityText: {
        fontSize: 12,
        color: '#FFFFFF',
    },
    itemMaximumPriorityText: {
        fontSize: 12,
        color: '#D50000',
    },
}));
